import { VesselLandingItem } from '@app/domain/entities/vessel-landing.entity';
import { PaginatedResponse } from '@app/shared/models/global.model';
import { VesselLandingItemDTO } from '../dtos/vessel-landing.dto';

const VESSEL_TYPE_LABELS: Record<string, string> = {
  GCO: 'GCO (All Others)',
  PURE_STEEL: 'Pure Steel',
  WOODEN_CRAFT: 'Wooden Craft (J1)',
  RORO: 'RORO (LCT)',
};

/** Vessel statuses that get no tag under the Vessel Name. */
const UNTAGGED_VESSEL_STATUSES = ['INCOMING', 'ALONGSIDE', 'SAILED'];

export class VesselLandingMapper {
  listToDomain(
    dto: PaginatedResponse<VesselLandingItemDTO>
  ): PaginatedResponse<VesselLandingItem> {
    return {
      ...dto,
      data: (dto.data || []).map(item => this.toDomain(item)),
    };
  }

  toDomain(dto: VesselLandingItemDTO): VesselLandingItem {
    return {
      id: dto.vesselByShiftId,
      vvcode: dto.vvcode == null ? '' : String(dto.vvcode),
      vesselName: dto.vesselName ?? '',
      vesselType: dto.vesselType ?? '',
      vesselTypeLabel: VESSEL_TYPE_LABELS[dto.vesselType] ?? dto.vesselType,
      voyIn: dto.voyIn ?? '',
      voyOut: dto.voyOut ?? '',
      berthNo: dto.berthNo ?? '',
      etb: dto.etb ?? null,
      etu: dto.etu ?? null,
      atb: dto.atb ?? null,
      atu: dto.atu ?? null,
      vesselStatus: dto.vesselStatus ?? null,
      isVesselClosed: !!dto.isVesselClosed,
      hasWaterBunkering: !!dto.hasWaterBunkering,
      tags: this._toTags(dto),
    };
  }

  /**
   * BE returns the string "null" for empty linerScheme. Treat it as null
   * and drop it from the Vessel Scheme filter options.
   */
  schemesToDomain(dto: string[]): string[] {
    return (dto || []).flatMap(scheme => {
      const parsed = this._parseScheme(scheme);
      return parsed == null ? [] : [parsed];
    });
  }

  clientCodesToDomain(dto: string[]): string[] {
    return (dto || []).filter(code => !!code);
  }

  private _parseScheme(scheme: string | null): string | null {
    if (scheme == null) return null;
    const value = String(scheme).trim();
    if (!value || value.toLowerCase() === 'null') return null;
    return value;
  }

  /**
   * Vessel Name tags, in display order: Vessel Scheme (any value, upper-cased — same as the
   * mobile app's LinerSchemeTag), LNG, NOM, PS, then status and Closed. The
   * Incoming / Alongside / Sailed statuses are not tagged.
   */
  private _toTags(dto: VesselLandingItemDTO): string[] {
    const tags: string[] = [];
    const scheme = this._parseScheme(dto.linerScheme);
    if (scheme) tags.push(scheme.toUpperCase());
    if (dto.isLNG) tags.push('LNG');
    if (dto.isNom) tags.push('NOM');
    if (dto.isPs) tags.push('PS');
    const status = dto.vesselStatus?.trim();
    if (status && !UNTAGGED_VESSEL_STATUSES.includes(status.toUpperCase())) {
      tags.push(status);
    }
    if (dto.isVesselClosed) tags.push('Closed');
    return tags;
  }
}
